import { useMutateUpdateChatTitle } from '@/hooks/mutation/useMutateUpdateChatTitle';
import { cn } from '@/lib/cn';
import { Input } from '@heidi/design-system/input';
import { typographyVariant } from '@heidi/design-system/typography';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { navV3Messages } from '../nav-v3.messages';

export interface ChatRowRenameInputProps {
  chatId: string;
  initialTitle: string;
  /** Called once editing ends, whichever way it ended (commit or cancel). */
  onDone: () => void;
}

/**
 * Inline rename input that replaces a nav chat row's visible label while
 * editing. Rendered by `RecentChatRow` via `SidebarMenuButton asChild` onto a
 * plain `div` rather than the row's usual literal `<button>` — an `<input>`
 * is interactive content and may not validly nest inside one, the same
 * reason `ChatSidebar`'s row swaps to a `div[role="button"]` while editing.
 *
 * Commits on Enter or blur, cancels on Escape, and treats an empty/unchanged
 * title as a no-op cancel rather than an API call.
 */
export const ChatRowRenameInput = ({
  chatId,
  initialTitle,
  onDone,
}: ChatRowRenameInputProps) => {
  const intl = useIntl();
  const { mutate: updateChatTitle } = useMutateUpdateChatTitle();
  const [title, setTitle] = useState(initialTitle);
  const inputRef = useRef<HTMLInputElement>(null);
  const doneRef = useRef(false);

  useEffect(() => {
    // Deferred a frame: the input mounts as the action menu's dropdown
    // closes, and `DropdownMenuContent`'s `onCloseAutoFocus` is suppressed
    // (see `ChatRowActionsMenu`) precisely so this focus grab — not Radix's
    // default focus-restore-to-trigger — decides where focus lands.
    const frame = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  const commit = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    const trimmed = title.trim();
    if (trimmed && trimmed !== initialTitle.trim()) {
      updateChatTitle({ chatId, title: trimmed });
    }
    onDone();
  }, [title, initialTitle, chatId, updateChatTitle, onDone]);

  const cancel = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone();
  }, [onDone]);

  return (
    <Input
      ref={inputRef}
      density="compact"
      value={title}
      onChange={(e) => setTitle(e.target.value)}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
      }}
      onBlur={commit}
      wrapperClassName={cn(
        'min-w-0 flex-1',
        // Resting border marks edit mode; suppress only the outer focus halo.
        'has-[:focus-visible]:ring-0'
      )}
      className={cn('h-full', typographyVariant({ variant: 'p2' }))}
      aria-label={intl.formatMessage(navV3Messages.chatRowRename)}
      data-testid="nav-chat-row-rename-input"
    />
  );
};
